import LayoutContainer from 'components/Common/LayoutContainer'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TierUpgradeCampaignTierCard from './TierUpgradeCampaignTierCard'

const HeroSection = styled.div`
  background-color: ${props => props.theme.palette.neutral.control.black100};
  color: ${props => props.theme.palette.neutral.default.eight};
`

const HeroGrid = styled.div`
  display: grid;
  gap: ${rem(32)};
  padding: ${rem(32)} 0;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    gap: ${rem(40)};
    align-items: center;
    padding: ${rem(64)} 0;
  }
`

const HeroImageContainer = styled.div`
  position: relative;
  border-radius: ${rem(8)};
  overflow: hidden;
  aspect-ratio: 4 / 3;

  ${mediaQueryUp.tablet} {
    aspect-ratio: 3 / 2;
  }
`

const PartnerLogos = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
`

const SocieteBrand = styled.div`
  display: flex;
  align-items: center;
`

const PartnerLogoImage = styled(ResponsiveImage)`
  img {
    max-height: ${rem(40)};
    max-width: ${rem(115)};
    width: auto;
    object-fit: contain;
  }
`

const FootnoteLink = styled.a`
  display: block;
  font-size: ${rem(14)};
  color: ${props => props.theme.palette.neutral.default.seven};
  text-decoration: underline;
`

interface Props {
  campaign: App.TierUpgradeCampaignPayload
  onCtaClick?: () => void
  ctaText?: string
  tierBenefits?: Array<App.LoyaltyRegionTierBenefit>
}

function TierUpgradeCampaignHero(props: Props) {
  const intl = useIntl()
  const { campaign, onCtaClick, ctaText, tierBenefits } = props

  const programName = getLoyaltyProgramDisplayName()
  const isSingleTier = campaign.tiers.length === 1
  const singleTier = campaign.tiers[0]

  const heroFootnote = intl.formatMessage({
    id: 'QPAO1F574xXjnvErbK_Q',
    defaultMessage: 'Terms and conditions apply',
    description: 'Terms and conditions footnote on the tier upgrade campaign hero',
  })

  return <HeroSection>
    <LayoutContainer variant="medium">
      <HeroGrid>
        <VerticalSpacer gap={20}>
          <VerticalSpacer gap={12}>
            <PartnerLogos>
              <SocieteBrand>
                <LuxuryEscapesSmallLogo height={24} fill="currentColor" />
                <LoyaltyProgramNameLogo width={123} height={24} />
              </SocieteBrand>
              <Divider kind="primary" direction="vertical" />
              <PartnerLogoImage
                id={campaign.partner.logoImageId}
                alt={campaign.partner.name}
              />
            </PartnerLogos>
            <Heading variant="heading1">{campaign.hero.title}</Heading>
            <BodyText variant="large">{campaign.hero.subtitle}</BodyText>
          </VerticalSpacer>
          <TextButton kind="primary" variant="ghost" onClick={onCtaClick}>
            {ctaText}
          </TextButton>
          <FootnoteLink href="#terms-and-conditions">
            {heroFootnote}
          </FootnoteLink>
        </VerticalSpacer>
        <CSSBreakpoint min="tablet">
          {isSingleTier && tierBenefits && <TierUpgradeCampaignTierCard
            tier={singleTier}
            title={intl.formatMessage({
              id: 'bA2W8cLSQMqXFJhUKlYN',
              defaultMessage: '{programName} {tierName} Benefits',
              description: 'Title for the single-tier benefit card in the hero section',
            }, { programName, tierName: getLoyaltyTierDisplayName(singleTier) })}
            benefits={tierBenefits}
          />}
          {!isSingleTier && <HeroImageContainer>
            <ResponsiveImage
              id={campaign.hero.imageId}
              fit="center"
              loading="eager"
              dpr={2}
            />
          </HeroImageContainer>}
        </CSSBreakpoint>
      </HeroGrid>
    </LayoutContainer>
  </HeroSection>
}

export default TierUpgradeCampaignHero
